/**
 * Full input/output disclosure for a tool step, mocked or live. Seeing both is what lets a
 * tester judge whether the simulated world matches what the real tools return.
 */

import { useState } from 'react';
import { FlaskConical } from 'lucide-react';
import { cn, prettyJson } from '@/lib/utils';
import { CopyButton } from './copy-button';

type Pane = 'input' | 'output';

interface StepIOProps {
  /** What the panes belong to, for the copy button's accessible name. */
  name: string;
  /** The call's arguments; absent or empty draws no Input pane. */
  input?: Record<string, unknown> | null;
  /** Absent draws no Output pane; null is a recorded "no output". */
  output?: unknown;
  simulated?: boolean;
}

const paneText = (pane: Pane, input: StepIOProps['input'], output: unknown): string | null => {
  if (pane === 'input') return prettyJson(input ?? {});
  if (output === null) return null;
  return typeof output === 'string' ? output : prettyJson(output);
};

export const StepIO = ({ name, input, output, simulated }: StepIOProps) => {
  const [openPane, setOpenPane] = useState<Pane | null>(null);
  const hasInput = Boolean(input && Object.keys(input).length > 0);
  const hasOutput = output !== undefined;
  if (!hasInput && !hasOutput) return null;

  const text = openPane ? paneText(openPane, input, output) : null;
  const pill = (pane: Pane) => (
    <button
      type='button'
      onClick={() => setOpenPane((current) => (current === pane ? null : pane))}
      className={cn(
        'rounded-full border px-2 py-px text-[9px] font-medium transition-colors',
        openPane === pane
          ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
          : 'border-border bg-background text-muted-foreground hover:bg-muted'
      )}
    >
      {pane === 'input' ? 'Input' : 'Output'}
    </button>
  );

  return (
    <div className='mt-1'>
      <div className='flex items-center gap-1'>
        {hasInput && pill('input')}
        {hasOutput && pill('output')}
        {simulated && (
          <span className='flex items-center gap-0.5 text-[9px] text-indigo-400'>
            <FlaskConical className='size-2.5' />
            simulated
          </span>
        )}
        {openPane && text !== null && <CopyButton text={text} label={`${name} ${openPane}`} />}
      </div>
      {openPane &&
        (text === null ? (
          <p className='mt-1 text-[10px] text-gray-800 italic'>No output recorded.</p>
        ) : (
          <pre className='mt-1 max-h-44 overflow-auto rounded-lg border bg-background/80 p-2 font-mono text-[9px] leading-relaxed text-foreground/75'>
            {text}
          </pre>
        ))}
    </div>
  );
};
